// Backstop for "paid but still in my cart". The success page only clears the cart if the customer lands on it in
// the SAME browser that holds the cart. It never does when they pay in a UPI app and come back later, when the
// confirmation arrives late via the webhook, or when they return in a different browser. So every checkout is
// recorded here (order ids + exactly which items) and later checked against the server: once ALL of its orders are
// paid, those items are removed from the cart. Pure functions take storage/fetch so they can be tested.
const KEY = "bloom_pending_checkouts";
const MAX_AGE_MS = 3 * 24 * 3600 * 1000; // an unresolved checkout is dropped after 3 days
const MAX_ENTRIES = 10;
const PAID = new Set(["confirmed", "refunded", "partially_refunded"]);

export const variationOf = (c) => c?.selectedVariation?.name || c?.selectedOption || c?.selectedCombo || c?.variation_name || null;

const read = (storage) => { try { const a = JSON.parse(storage.getItem(KEY) || "[]"); return Array.isArray(a) ? a : []; } catch { return []; } };
const write = (storage, list) => { try { if (list.length) storage.setItem(KEY, JSON.stringify(list.slice(-MAX_ENTRIES))); else storage.removeItem(KEY); } catch { /* storage full / blocked */ } };
const live = (list, now) => list.filter((e) => e && Array.isArray(e.ids) && e.ids.length && Array.isArray(e.items) && now - (e.t || 0) < MAX_AGE_MS);

export function recordPendingCheckout({ ids, items }, storage = localStorage, now = Date.now()) {
  const cleanIds = (ids || []).filter(Boolean);
  const cleanItems = (items || []).filter((i) => i && i.id != null).map((i) => ({ id: i.id, variation: variationOf(i) }));
  if (!cleanIds.length || !cleanItems.length) return;
  const rest = live(read(storage), now).filter((e) => e.ids.join() !== cleanIds.join());
  write(storage, [...rest, { ids: cleanIds, items: cleanItems, t: now }]);
}

export function dropPendingCheckout(ids, storage = localStorage, now = Date.now()) {
  const key = (ids || []).filter(Boolean).join();
  write(storage, live(read(storage), now).filter((e) => e.ids.join() !== key));
}

// entries + {orderId: status|null} → which items to remove and which entries to keep waiting.
//   all orders paid            → remove the items, drop the entry
//   any order still pending    → keep waiting
//   otherwise (cancelled/unknown, or a mixed outcome we can't map to items) → drop, remove nothing
export function planReconcile(entries, statuses) {
  const remove = []; const keep = [];
  for (const e of entries) {
    const st = e.ids.map((id) => statuses[id]);
    if (st.every((s) => PAID.has(s))) remove.push(...e.items);
    else if (st.some((s) => s === "pending")) keep.push(e);
  }
  return { remove, keep };
}

let inFlight = false;
// Returns the number of items removed. Never throws; on any network/server problem it keeps every entry for next time.
export async function checkPendingCheckouts({ backendUrl, onPaid, storage = localStorage, fetchFn = fetch, now = Date.now() }) {
  if (inFlight) return 0;
  const entries = live(read(storage), now);
  if (entries.length !== read(storage).length) write(storage, entries);
  if (!entries.length) return 0;
  inFlight = true;
  try {
    const ids = [...new Set(entries.flatMap((e) => e.ids))];
    const r = await fetchFn(`${backendUrl}/api/order-status?ids=${encodeURIComponent(ids.join(","))}`);
    if (!r.ok) return 0;
    const { statuses } = await r.json();
    if (!statuses || typeof statuses !== "object") return 0;
    const { remove, keep } = planReconcile(entries, statuses);
    if (remove.length) onPaid(remove);
    // Re-read so a checkout recorded while the request was in flight isn't lost.
    const fresh = live(read(storage), now);
    const handled = new Set(entries.map((e) => e.ids.join()));
    const kept = new Set(keep.map((e) => e.ids.join()));
    write(storage, fresh.filter((e) => !handled.has(e.ids.join()) || kept.has(e.ids.join())));
    return remove.length;
  } catch { return 0; } finally { inFlight = false; }
}
